import { LaneFilter, QuoteBidStatus } from '@shared/generated/graphql';
import { LaneHistoryDefaults } from '@shared/hooks/laneHistoryDefaults';
import { Quote } from '@shared/types/quote';
import { getEquipmentTypesFromFreightOrder } from 'clerk_common/templates/freight_order/fieldExtractors/fieldExtractors';
import { useEffect, useState } from 'react';
import {
  getBuiltInLaneHistoryDefaults,
  getLaneHistoryDefaults,
} from './laneHistoryDefaults';

export type LaneHistoryFilters = {
  laneFilter?: LaneFilter;
  equipmentTypes?: string[];
  originatorIds?: string[];
  statuses?: QuoteBidStatus[];
};

type UseGetQuotesState = {
  filters: LaneHistoryFilters;
  setFilters: (filters: LaneHistoryFilters) => void;
  resetFilters: () => void;
};

export const getLaneFilter = (quote: Quote): LaneFilter | undefined => {
  return getLaneFilterWithDefaults(
    quote,
    getLaneHistoryDefaults(quote.organization.id)
  );
};

const getBuiltInLaneFilter = (quote: Quote): LaneFilter | undefined => {
  return getLaneFilterWithDefaults(quote, getBuiltInLaneHistoryDefaults());
};

const getLaneFilterWithDefaults = (
  quote: Quote,
  laneHistoryDefaults: LaneHistoryDefaults
): LaneFilter | undefined => {
  const route = quote.routeGeoCoordinates;
  if (!route || route?.length < 1) return undefined;
  const destinationIndex = route.length - 1;
  return {
    destination: {
      latitude: route[destinationIndex].latitude,
      longitude: route[destinationIndex].longitude,
      radiusMiles: laneHistoryDefaults.destinationRadiusMiles,
    },
    origin: {
      latitude: route[0].latitude,
      longitude: route[0].longitude,
      radiusMiles: laneHistoryDefaults.originRadiusMiles,
    },
  };
};

const getEquipmentTypes = (quote: Quote): string[] => {
  if (!quote?.extractedData) return [];
  return getEquipmentTypesFromFreightOrder(quote.extractedData);
};

export const useLaneHistoryFilters = (quote: Quote): UseGetQuotesState => {
  const [filters, setFilters] = useState<LaneHistoryFilters>(
    toDefaultFilters(quote)
  );

  const resetFilters = () => {
    setFilters(toBuiltInDefaultFilters(quote));
  };

  useEffect(() => {
    setFilters(toDefaultFilters(quote));
  }, [quote.id]);

  return {
    filters,
    setFilters,
    resetFilters,
  };
};

export const toDefaultFilters = (quote: Quote): LaneHistoryFilters => {
  return toFilters(quote, getLaneFilter);
};

export const toBuiltInDefaultFilters = (quote: Quote): LaneHistoryFilters => {
  return toFilters(quote, getBuiltInLaneFilter);
};

const toFilters = (
  quote: Quote,
  getFilter: (quote: Quote) => LaneFilter | undefined
): LaneHistoryFilters => {
  const originatorId = quote.originator?.id;
  return {
    originatorIds: originatorId ? [originatorId] : undefined,
    statuses: [QuoteBidStatus.WON, QuoteBidStatus.LOST, QuoteBidStatus.QUOTED],
    laneFilter: getFilter(quote),
    equipmentTypes: getEquipmentTypes(quote),
  };
};
